import {
    createContext,
    type PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { WishlistSummary } from '@/lib/api/wishlist.server';

/* eslint-disable react-refresh/only-export-components */

export type WishlistMembership = {
    listId: string;
    listName: string;
    itemId: string;
};

type WishlistContextValue = {
    wishlists: WishlistSummary[];
    productMembership: Record<string, WishlistMembership[]>;
    setWishlists: (wishlists: WishlistSummary[]) => void;
    isProductInAnyWishlist: (productId: string | null | undefined) => boolean;
    getProductMembership: (productId: string | null | undefined) => WishlistMembership[];
};

const defaultWishlistContextValue: WishlistContextValue = {
    wishlists: [],
    productMembership: {},
    setWishlists: () => undefined,
    isProductInAnyWishlist: () => false,
    getProductMembership: () => [],
};

const WishlistContext = createContext<WishlistContextValue>(defaultWishlistContextValue);

const EMPTY_WISHLIST_MEMBERSHIP: WishlistMembership[] = [];

const buildProductMembership = (wishlists: WishlistSummary[]): Record<string, WishlistMembership[]> => {
    return wishlists.reduce<Record<string, WishlistMembership[]>>((membership, wishlist) => {
        wishlist.items.forEach((item) => {
            membership[item.productId] = [
                ...(membership[item.productId] ?? []),
                {
                    listId: wishlist.id,
                    listName: wishlist.name,
                    itemId: item.id,
                },
            ];
        });

        return membership;
    }, {});
};

type WishlistProviderInitialWishlists = Promise<WishlistSummary[]> | WishlistSummary[];

const WishlistProvider = ({
    children,
    initialWishlists,
}: PropsWithChildren<{ initialWishlists?: WishlistProviderInitialWishlists }>) => {
    const localUpdateRef = useRef(false);
    const [wishlists, setWishlistsState] = useState<WishlistSummary[]>(() =>
        Array.isArray(initialWishlists) ? initialWishlists : []
    );

    useEffect(() => {
        localUpdateRef.current = false;

        if (!initialWishlists) {
            setWishlistsState([]);
            return;
        }

        if (Array.isArray(initialWishlists)) {
            setWishlistsState((current) => {
                if (JSON.stringify(current) === JSON.stringify(initialWishlists)) {
                    return current;
                }
                return initialWishlists;
            });
            return;
        }

        let active = true;
        void initialWishlists.then((nextWishlists) => {
            if (active && !localUpdateRef.current) {
                setWishlistsState((current) => {
                    if (JSON.stringify(current) === JSON.stringify(nextWishlists)) {
                        return current;
                    }
                    return nextWishlists;
                });
            }
        });

        return () => {
            active = false;
        };
    }, [initialWishlists]);

    const productMembership = useMemo(() => buildProductMembership(wishlists), [wishlists]);

    const setWishlists = useCallback((nextWishlists: WishlistSummary[]) => {
        localUpdateRef.current = true;
        setWishlistsState(nextWishlists);
    }, []);

    const isProductInAnyWishlist = useCallback(
        (productId: string | null | undefined) => Boolean(productId && productMembership[productId]?.length),
        [productMembership]
    );

    const getProductMembership = useCallback(
        (productId: string | null | undefined) =>
            productId ? (productMembership[productId] ?? EMPTY_WISHLIST_MEMBERSHIP) : EMPTY_WISHLIST_MEMBERSHIP,
        [productMembership]
    );

    const value = useMemo(
        () => ({
            wishlists,
            productMembership,
            setWishlists,
            isProductInAnyWishlist,
            getProductMembership,
        }),
        [wishlists, productMembership, setWishlists, isProductInAnyWishlist, getProductMembership]
    );

    return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlistContext = (): WishlistContextValue => {
    return useContext(WishlistContext);
};

export const useWishlists = (): WishlistSummary[] => {
    return useWishlistContext().wishlists;
};

export const useWishlistUpdater = (): ((wishlists: WishlistSummary[]) => void) => {
    return useWishlistContext().setWishlists;
};

export const useIsProductInAnyWishlist = (productId: string | null | undefined): boolean => {
    return useWishlistContext().isProductInAnyWishlist(productId);
};

export const useWishlistMembership = (productId: string | null | undefined): WishlistMembership[] => {
    return useWishlistContext().getProductMembership(productId);
};

export default WishlistProvider;
